import {
  lighten,
  transparentize,
} from '@nobia/zeus-components/lib/helpers/polished'
import rem from '@nobia/zeus-components/lib/styled/rem'
import { getTheme } from '@nobia/zeus-components/lib/themes'

const primaryColor = '#31261D'
const secondaryColor = '#31261D'
const thirdColor = '#FBF9F6'
const black = '#31261D'
const white = '#FBF9F6'

const tone1 = '#E4D4C4'
const tone2 = '#F2E9DB'
const tone3 = '#F8F2EC'
const tone4 = '#6C645A'

const item1 = '#b0ada8'
const item2 = '#d7d7d7'
const item3 = '#ece8e5'
const item4 = '#f4f2f0'

const blue = '#1E44CB'
const lineColor = '#C3B5A7'

const success = '#04582F'
const info = '#00d0bd'
const warning = '#872E2E'
const error = '#872E2E'

const textColor = '#31261D'

const theme = getTheme('marbodal')

export default {
  base: {
    backgroundColor: tone2,
    fontFamily: 'Lausanne',
    borderRadius: rem(3),
  },
  horizontalScrollable: {
    fade: {
      linearGradientLeft: `linear-gradient(
        to left,
        rgba(242, 233, 219, 0) 0%,
        rgba(242, 233, 219, 0.9) 90%,
        rgb(242, 233, 219) 100%
      )`,
      linearGradientRight: `linear-gradient(
        to right,
        rgba(242, 233, 219, 0) 0%,
        rgba(242, 233, 219, 0.3) 20%,
        rgba(242, 233, 219, 0.9) 60%,
        rgb(242, 233, 219) 100%
      )`,
    },
    arrow: {
      linearGradientLeft: `linear-gradient(
        to left,
        rgba(242, 233, 219, 0) 0%,
        rgba(242, 233, 219, 0.1) 50%,
        rgba(242, 233, 219, 0.2) 60%,
        rgba(242, 233, 219, 0.5) 80%,
        rgb(242, 233, 219) 100%
      )`,
      linearGradientRight: `linear-gradient(
        to right,
        rgba(242, 233, 219, 0) 0%,
        rgba(242, 233, 219, 0.1) 10%,
        rgba(242, 233, 219, 0.3) 20%,
        rgb(242, 233, 219) 85%
      )`,
    },
  },
  colors: {
    primary: primaryColor,
    secondary: secondaryColor,
    third: thirdColor,
    black,
    white,
    tone1,
    tone2,
    tone3,
    tone4,
    item1,
    item2,
    item3,
    item4,
    success,
    info,
    warning,
    error,
    darkBlue: primaryColor,
    lighterBlue: '#dfe6f6',
    lightBlue: '#5f7ed2',
    lightBlueTransparent: '#5f7ed244',
    textColor,
    lineColor: '#C3B5A7',
  },
  header: {
    wrapper: {
      background: tone2,
      color: textColor,
      border: `1px solid transparent`,
      height: theme.rem(66),
      md: {
        height: theme.rem(88),
      },
    },
    title: {
      fontWeight: 250,
      fontSize: 32,
      lineHeight: 42,
    },
    logo: {
      marginLeft: 0,
      width: theme.rem(141),
      md: {
        width: theme.rem(168),
      },
    },
  },
  close: {
    background: tone1,
  },
  navigation: {
    wrapper: {
      background: tone2,
      border: `1px solid ${lineColor}`,
      padding: `${theme.rem(7)} 0`,
      lg: {
        padding: `${theme.rem(17)} ${theme.rem(19)}`,
      },
    },
    item: {
      color: textColor,
      activeColor: textColor,
      activeBackgroundColor: tone1,
      borderRadius: 3,
      fontWeight: 350,
      fontSize: 12,
      lg: {
        fontSize: 15,
      },
    },
  },
  home: {
    border: `1px solid ${lineColor}`,
    image: {
      borderRadius: rem(3),
    },
  },
  tabs: {
    background: tone1,
    color: textColor,
    backgroundColor: lineColor,
    activeColor: textColor,
    activeBackgroundColor: tone2,
  },
  buttons: {
    textTransform: 'default',
    borderRadius: 3,
    fontWeight: 350,
    lineHeight: 22,
    padding: {
      small: `${theme.rem(6)}`,
      default: `${theme.rem(12)} ${theme.rem(30)} ${theme.rem(14)}`,
      large: `${theme.rem(21)} ${theme.rem(42)} ${theme.rem(20)}`,
    },
    fontSize: {
      small: 13,
      default: 16,
      large: 16,
    },
    primary: {
      color: white,
      background: blue,
      activeBackground: transparentize(0.15, blue),
    },
    outlined: {
      color: white,
      background: primaryColor,
      activeBackground: transparentize(0.15, primaryColor),
    },
    secondary: {
      color: white,
      background: secondaryColor,
      activeBackground: lighten(0.15, secondaryColor),
    },
    disabled: {
      color: white,
      background: lighten(0.75, primaryColor),
    },
  },
  modal: {
    background: tone2,
  },
  forms: {
    background: tone2,
    borderRadius: 3,
    height: rem(50),
    fontSize: rem(14),
    fontFamily: '"Lausanne", "Arial", sans-serif',
    fontWeight: 350,
    colors: {
      border: lineColor,
      selected: primaryColor,
      errorBackground: '#f0e8db',
      errorColor: textColor,
    },
  },
  typography: {
    heading1: {
      fontFamily: 'Reckless',
      fontSize: 36,
      lineHeight: 40,
      fontWeight: 350,
      color: textColor,
      md: {
        fontSize: 66,
        lineHeight: 71,
      },
    },
    heading2: {
      fontFamily: 'Reckless',
      fontSize: 30,
      lineHeight: 36,
      fontWeight: 350,
      color: textColor,
      md: {
        fontSize: 46,
        lineHeight: 50,
      },
    },
    heading3: {
      fontFamily: 'Lausanne',
      fontSize: 26,
      lineHeight: 30,
      fontWeight: 650,
      color: textColor,
      md: {
        fontSize: 32,
        lineHeight: 42,
      },
    },
    heading4: {
      fontFamily: 'Lausanne',
      fontSize: 18,
      lineHeight: 24,
      fontWeight: 650,
      color: textColor,
      md: {
        fontSize: 20,
        lineHeight: 26,
      },
    },
    heading5: {
      fontFamily: 'Lausanne',
      fontSize: 16,
      lineHeight: 22,
      fontWeight: 650,
      color: textColor,
      md: {
        fontSize: 18,
        lineHeight: 24,
      },
    },
    heading6: {
      fontFamily: 'Lausanne',
      fontSize: 16,
      lineHeight: 22,
      fontWeight: 650,
      color: textColor,
    },
    preamble: {
      fontFamily: 'Lausanne',
      fontWeight: 250,
      fontSize: 21,
      lineHeight: 30,
      md: {
        fontSize: 26,
        lineHeight: 36,
      },
    },
    preambleB: {
      fontFamily: 'Lausanne',
      fontWeight: 250,
      fontSize: 18,
      lineHeight: 28,
      md: {
        fontSize: 21,
        lineHeight: 31,
      },
    },
    paragraphLead: {
      fontWeight: 350,
      fontSize: 18,
      lineHeight: 26,
    },
    paragraph: {
      fontWeight: 350,
      fontSize: 16,
      lineHeight: 24,
      color: textColor,
      fontFamily: 'Lausanne',
    },
    paragraphSmall: {
      fontWeight: 350,
      fontSize: 13,
      lineHeight: 18,
      color: textColor,
      md: {
        fontSize: 14,
        lineHeight: 19,
      },
    },
    paragraphByline: {
      fontWeight: 350,
      fontSize: 13,
      lineHeight: 18,
      color: '#7A7A7A',
      md: {
        fontSize: 14,
        lineHeight: 19,
      },
    },
    link: {
      fontWeight: 350,
      color: primaryColor,
    },
    input: {
      fontSize: 16,
    },
  },
  saved: {
    image: {
      borderRadius: rem(3),
      boxShadow: 'none',
      backgroundColor: '#FFFFFF',
      largeProducts: {
        width: '180px',
        height: '180px',
        md: {
          width: '355px',
          height: '355px',
        },
      },
      smallProducts: {
        width: '180px',
        height: '180px',
        md: {
          width: '355px',
          height: '355px',
        },
      },
      kitchenQuizProducts: {
        width: '180px',
        height: '180px',
        md: {
          width: '180px',
          height: '180px',
        },
      },
    },
    sectionHeading: {
      fontFamily: 'Reckless',
      fontSize: rem(24),
      fontWeight: 350,
      lineHeight: rem(26),
      md: {
        fontSize: rem(32),
        lineHeight: rem(42),
      },
    },
    product: {
      block: {
        padding: '0',
      },
      label: {
        color: textColor,
        fontWeight: 650,
        fontSize: rem(18),
      },
      margins: {
        small: rem(16),
        large: rem(50),
        md: {
          small: rem(40),
          large: rem(64),
        },
      },
      name: {
        color: textColor,
        fontSize: rem(18),
        fontWeight: 650,
        lineHeight: rem(26),
        md: {
          fontSize: rem(18),
          lineHeight: rem(26),
        },
        smallProduct: {
          fontWeight: 650,
        },
      },
      kitchenQuiz: {
        itemParagraph: 350,
        description: 350,
      },
      description: {
        color: textColor,
        fontSize: rem(16),
        fontWeight: 350,
        lineHeight: rem(24),
        md: {
          fontSize: rem(16),
          lineHeight: rem(24),
        },
        smallProduct: {
          fontWeight: 350,
        },
      },
    },
  },
  appointment: {
    appointment: {
      background: tone1,
      padding: '1px 25px',
      margin: '20px',
      heading: {
        fontSize: theme.rem(14),
        fontWeight: 650,
        lineHeight: theme.rem(19),
      },
      text: {
        fontSize: theme.rem(14),
        fontWeight: 350,
        lineHeight: theme.rem(19),
      },
      icon: {
        background: black,
        color: white,
      },
      hr: {
        color: 'transparent',
      },
    },
    prepare: {
      heading: {
        fontSize: theme.rem(16),
        fontWeight: 650,
        lineHeight: theme.rem(24),
      },
      text: {
        fontSize: theme.rem(14),
        fontWeight: 350,
        lineHeight: theme.rem(19),
      },
    },
  },
  step: {
    margins: '0 0 50px 0',
    checkedStep: {
      border: `1px solid ${lineColor}`,
    },
    uncheckedStep: {
      border: `1px solid transparent`,
      background: tone1,
    },
    background: tone2,
    borderRadius: '3px',
    checkline: {
      width: '1px',
      colors: {
        default: lineColor,
        checked: 'transparent',
      },
      position: {
        height: '75px',
        top: '-50px',
      },
    },
  },
  order: {
    background: white,
    border: `1px solid ${lineColor}`,
    borderRadius: rem(3),
    orderCancelled: {
      errorTextColor: error,
    },
    selectItem: {
      paddingBottom: '20px',
      padding: '20px',
      evenColor: white,
      unEvenColor: 'rgba(228,212,196,0.3)',
    },
    delivery: {
      background: tone1,
    },
    label: {
      fontWeight: 650,
    },
  },
  overlayBada: {
    icon: {
      background: tone2,
      color: black,
    },
  },
  checkbox: {
    color: blue,
    background: white,
    uncheckedBackground: white,
    borderColor: white,
    size: 44,
    strokeWidth: 3,
    strokeSize: 0.8,
  },
  loadingIndicator: {
    color: lineColor,
  },
  help: {
    topBackground: tone1,
    background: tone2,
    borderRadius: rem(0),
    border: `1px solid ${lineColor}`,
    tabs: {
      color: textColor,
      indicatorColor: blue,
    },
  },
  spacings: {
    xxxs: 2,
    xxs: 4,
    xs: 8,
    s: 12,
    m: 16,
    l: 24,
    xl: 32,
    xxl: 64,
    xxxl: 96,
  },
  multiStorePage: {
    background: tone2,
    alignItems: 'center',
    maxWidth: rem(849),
    storeSelector: {
      background: tone1,
      border: 'none',
      borderRadius: '3px',
    },
  },
  designs: {
    border: `1px solid ${lineColor}`,
  },
  carousel: {
    border: 'none',
    borderRadius: 0,
    selector: {
      position: 'static',
      margin: '10px 20px 0 20px',
    },
  },
  messages: {
    sendMessage: {
      background: tone1,
      borderColor: lineColor,
      button: {
        background: primaryColor,
        color: white,
      },
    },
    bubble: {
      store: tone3,
      customer: tone1,
      initials: {
        background: primaryColor,
      },
    },
    input: {
      borderColor: lineColor,
    },
    textarea: {
      borderColor: lineColor,
    },
    newMessages: {
      borderColor: lineColor,
      color: blue,
    },
  },
}
